import firebase from "firebase/compat/app";
import "firebase/compat/storage";
import "firebase/compat/firestore";
import { nanoid } from "nanoid";

export class ProgressPhotoUploadError extends Error {
  constructor(message, { code, userMessage, cause } = {}) {
    super(message);
    this.name = "ProgressPhotoUploadError";
    this.code = code || "UPLOAD_FAILED";
    this.userMessage =
      userMessage || "Photos could not be uploaded. Please try again.";
    if (cause) {
      this.cause = cause;
    }
  }
}

async function rollbackUploads(uploaded) {
  await Promise.all(
    uploaded.map(async (entry) => {
      try {
        await firebase.storage().ref(entry.storagePath).delete();
      } catch (_) {
        // best-effort
      }
      try {
        await entry.docRef.delete();
      } catch (_) {
        // best-effort
      }
    }),
  );
}

/**
 * Upload a progress photo group for a client (coach console).
 * Matches mobile schema/paths so existing gallery, compare, and AI tools work unchanged.
 */
export async function saveProgressPhotoGroup({
  organizationId,
  clientId,
  photos,
  groupId,
  createdOn,
  configuration,
  uploadedByUid,
}) {
  if (!organizationId || !clientId) {
    throw new ProgressPhotoUploadError("Missing organization or client", {
      code: "MISSING_CONTEXT",
    });
  }

  if (!photos?.length) {
    throw new ProgressPhotoUploadError("No photos selected", {
      code: "NO_PHOTOS",
      userMessage: "Please select at least one photo to upload.",
    });
  }

  const uploaded = [];

  try {
    for (const { file, type } of photos) {
      if (!file) {
        throw new ProgressPhotoUploadError(`Missing file for pose: ${type}`, {
          code: "MISSING_FILE",
        });
      }

      const newId = nanoid();
      const storagePath = `${organizationId}/progress_photos/${clientId}/${newId}.jpg`;
      const storageRef = firebase.storage().ref(storagePath);

      await storageRef.put(file, {
        contentType: file.type || "image/jpeg",
        cacheControl: "public,max-age=31536000",
      });
      const url = await storageRef.getDownloadURL();

      const docRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("progressPhotos")
        .doc(newId);

      await docRef.set(
        {
          url,
          id: newId,
          clientId,
          createdOn,
          type,
          groupId,
          configuration,
          uploadedByUid: uploadedByUid || null,
          uploadSource: "coach",
        },
        { merge: true },
      );

      uploaded.push({ storagePath, docId: newId, docRef });
    }

    return {
      success: true,
      groupId,
      uploadedCount: uploaded.length,
    };
  } catch (error) {
    if (uploaded.length > 0) {
      await rollbackUploads(uploaded);
    }

    if (error instanceof ProgressPhotoUploadError) {
      throw error;
    }

    throw new ProgressPhotoUploadError(
      error?.message || "Progress photo upload failed",
      { cause: error },
    );
  }
}
